import type { PostureOrientationLabel } from './strategies/posture-orientation-strategy-types';

const ORIENTATION_EVIDENCE_SKIP = [
  /^Tie-break:/i,
  /^SoP table/i,
  /ASIS\/PSIS needs to be set/i,
  /^Ribcage Aligned over Pelvis$/i,
  /^No Significant Deviations Detected$/i,
];

type EvidenceRule = {
  test: RegExp;
  format: (match: RegExpMatchArray) => string;
};

/** Map orientation evidence lines to compact chip labels (matched classification points). */
const ORIENTATION_EVIDENCE_RULES: EvidenceRule[] = [
  {
    test: /Anterior pelvic orientation \(~([\d.]+)°/i,
    format: (m) => `Anterior pelvic tilt ${m[1]}°`,
  },
  {
    test: /Anterior Pelvic Tilt ([\d.]+)° \(Clinical/i,
    format: (m) => `Anterior pelvic tilt ${m[1]}°`,
  },
  {
    test: /Mild Anterior Pelvic Tilt ([\d.]+)°/i,
    format: (m) => `Mild anterior pelvic tilt ${m[1]}°`,
  },
  {
    test: /Hip stack not far forward of ankle \(([\d.]+) cm/i,
    format: (m) => `Hip not far forward ${m[1]}cm`,
  },
  {
    test: /Hip markedly anterior to ankle plumb \(([\d.]+) cm/i,
    format: (m) => `Hip anterior ${m[1]}cm`,
  },
  {
    test: /Hip anterior to ankle plumb \(([\d.]+) cm/i,
    format: (m) => `Hip mild anterior ${m[1]}cm`,
  },
  {
    test: /Hip Anterior Shift ([\d.]+)cm \(Clinical/i,
    format: (m) => `Hip anterior ${m[1]}cm`,
  },
  {
    test: /Mild Hip Anterior Shift ([\d.]+)cm/i,
    format: (m) => `Hip mild anterior ${m[1]}cm`,
  },
  {
    test: /Shoulders posterior to hips vs plumb.*Δ ≈ ([\d.-]+) cm/i,
    format: (m) => `Torso posterior Δ${m[1]}cm`,
  },
  {
    test: /Mild posterior shoulder shift vs hips \(Δ ≈ ([\d.-]+) cm\)/i,
    format: (m) => `Mild torso posterior Δ${m[1]}cm`,
  },
  {
    test: /Shoulders anterior to hips vs plumb \(Δ ≈ ([\d.-]+) cm\)/i,
    format: (m) => `Torso anterior Δ${m[1]}cm`,
  },
  {
    test: /Mild forward trunk vs hips \(Δ ≈ ([\d.-]+) cm\)/i,
    format: (m) => `Mild torso anterior Δ${m[1]}cm`,
  },
  {
    test: /Pelvic line suggests posterior tilt \(~([\d.-]+)°\)/i,
    format: (m) => `Posterior pelvic tilt ${m[1]}°`,
  },
  {
    test: /Knee flexion beyond [\d.]+° \(~([\d.]+)°/i,
    format: (m) => `Knee flexion ${m[1]}°`,
  },
  {
    test: /^Knee flexion beyond/i,
    format: () => 'Knee flexion',
  },
  {
    test: /^Knee Flexion ([\d.]+)°$/i,
    format: (m) => `Knee flexion ${m[1]}°`,
  },
  {
    test: /^Knee Flexion$/i,
    format: () => 'Knee flexion',
  },
  {
    test: /^Knee hyperextension pattern/i,
    format: () => 'Knee hyperextension',
  },
  {
    test: /^Knee Hyperextension ([\d.]+)°$/i,
    format: (m) => `Knee hyperextension ${m[1]}°`,
  },
  {
    test: /^Knee Hyperextension$/i,
    format: () => 'Knee hyperextension',
  },
  {
    test: /^Global Forward Lean \(Shoulders Ant ([\d.]+)cm\)/i,
    format: (m) => `Global forward lean ${m[1]}cm`,
  },
  {
    test: /^Forward Torso Lean$/i,
    format: () => 'Forward torso lean',
  },
  {
    test: /^Mild Forward Torso Lean$/i,
    format: () => 'Mild forward torso lean',
  },
  {
    test: /^Torso Sway \(Ribcage behind Pelvis\)$/i,
    format: () => 'Torso sway',
  },
  {
    test: /^Mild Torso Sway$/i,
    format: () => 'Mild torso sway',
  },
  {
    test: /^APT subtype: Forward Lean/i,
    format: () => 'APT forward lean subtype',
  },
  {
    test: /No strong swayback vs anterior-orientation separation/i,
    format: () => 'Balanced sagittal pattern',
  },
];

const LATERAL_REASON_SHORT: Record<string, string> = {
  'Head Side-bent Left (Right Ear Higher)': 'Head side-bent L',
  'Head Side-bent Right (Left Ear Higher)': 'Head side-bent R',
  'Right Shoulder Lower': 'R shoulder lower',
  'Left Shoulder Lower': 'L shoulder lower',
  'Right Hip Lower': 'R hip lower',
  'Left Hip Lower': 'L hip lower',
  'Pelvic Shift Right': 'Pelvic shift R',
  'Pelvic Shift Left': 'Pelvic shift L',
};

const LATERAL_REASON_SKIP = [
  /^Normal balanced lateral alignment$/i,
  /^Awaiting Anterior or Posterior view for lateralization analysis$/i,
];

function normalizeTag(tag: string): string {
  return tag.toLowerCase().replace(/\s+/g, ' ').trim();
}

function orientationEvidenceToShortTag(line: string): string | null {
  const trimmed = line.trim().replace(/^\[\+\d+\]\s*/, '');
  if (!trimmed) return null;
  if (ORIENTATION_EVIDENCE_SKIP.some((re) => re.test(trimmed))) return null;

  for (const rule of ORIENTATION_EVIDENCE_RULES) {
    const match = trimmed.match(rule.test);
    if (match) return rule.format(match);
  }

  const beforeParen = trimmed.split('(')[0]?.trim();
  if (beforeParen && beforeParen.length <= 48) return beforeParen;
  return trimmed.length <= 48 ? trimmed : `${trimmed.slice(0, 45).trim()}…`;
}

function lateralReasonToShortTag(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed || LATERAL_REASON_SKIP.some((re) => re.test(trimmed))) return null;

  if (trimmed.startsWith('Foot Presentation (Posterior):')) {
    return 'Foot presentation';
  }

  return LATERAL_REASON_SHORT[trimmed] ?? trimmed;
}

export interface BuildClassificationSignalsInput {
  orientation: PostureOrientationLabel;
  orientationEvidence: string[];
  lateralReasons: string[];
  stackedLabel: string;
  forwardHeadDeg?: number;
  forwardHeadThresholdDeg: number;
  lateralized?: string;
}

/**
 * Build compact signal chips for exports and the Signals accordion.
 * Includes every matched orientation point (strategy evidence) plus lateral/FHP tags.
 */
export function buildClassificationSignals(input: BuildClassificationSignalsInput): string[] {
  const tags: string[] = [];
  const seen = new Set<string>();

  const push = (tag: string | null | undefined) => {
    const value = tag?.trim();
    if (!value) return;
    const key = normalizeTag(value);
    if (seen.has(key)) return;
    seen.add(key);
    tags.push(value);
  };

  for (const line of input.orientationEvidence) {
    push(orientationEvidenceToShortTag(line));
  }

  for (const line of input.lateralReasons) {
    push(lateralReasonToShortTag(line));
  }

  if (
    isFinite(input.forwardHeadDeg) &&
    (input.forwardHeadDeg as number) >= input.forwardHeadThresholdDeg
  ) {
    push(`Forward head ${(input.forwardHeadDeg as number).toFixed(0)}°`);
  }

  if (input.lateralized && input.lateralized !== 'Balanced') {
    push(`${input.lateralized} lateralization`);
  }

  if (tags.length === 0) {
    push(input.orientation === 'Stacked' ? input.stackedLabel : 'Ideally stacked');
  }

  return tags;
}
